/* eslint-disable react/no-danger */
/* product description is html */
import React, { FunctionComponent } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Image } from '@empiriecom/module-components/Image';
import { Product } from '@/src/interfaces';
import { useSeo } from '../ProductContext';

const BrandImageAndHeadline = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
`;

const BrandImageWrapper = styled.div`
  margin: 0 0.5rem 0 0.75rem;
`;

const BrandImage = styled(Image)`
  max-width: 3.15rem;
  width: auto;
  max-height: 3.15rem;
  height: auto;
  overflow: hidden;
`;

const NameAndOrdernumber = styled.div`
  display: flex;
  flex-flow: column wrap;
  justify-content: flex-start;
  align-items: flex-start;
  margin: 0.25rem 0;
  width: 100%;
`;

const ProductName = styled.h2`
  font-size: 1rem;
  font-weight: bold;
  margin: 0;
  width: 100%;
`;

const OrderNumber = styled.span`
  color: #a0a0a0;
  font-size: 0.75rem;
`;

interface ProductHeadlineProps {
  product: Product;
}

/**
 * This is the product headline above the product description. If a brand image for the
 * given product is available it will display the brand image, otherwise no image will
 * be displayed. The product name as well as the order number will always be displayed.
 */
export const ProductHeadline: FunctionComponent<ProductHeadlineProps> = ({
  product,
}): JSX.Element => {
  const { seoData } = useSeo();
  const BrandImageElement = product.brand?.image ? (
    <BrandImage src={product.brand.image} alt={product.brand.name} fallback={() => null} />
  ) : null;
  return (
    <BrandImageAndHeadline data-testid="product-headline">
      <BrandImageWrapper>
        {product.brand?.image ? (
          <>
            {seoData?.brandLink ? (
              <a href={seoData.brandLink.url} data-testid="product-headline-brand-link">
                {BrandImageElement}
              </a>
            ) : (
              <span data-testid="product-headline-brand-container">{BrandImageElement}</span>
            )}
          </>
        ) : null}
      </BrandImageWrapper>
      <NameAndOrdernumber>
        <ProductName data-testid="product-headline-product-name">{product.name}</ProductName>
        <OrderNumber data-testid="product-headline-order-number">
          <FormattedMessage
            id="page.brandlogo-headline"
            defaultMessage="Art.-Nr.: {data}"
            description="The product ordernumber displayed next to the brand logo"
            values={{
              data: product.orderNumber.concat(product.promotion),
            }}
          />
        </OrderNumber>
      </NameAndOrdernumber>
    </BrandImageAndHeadline>
  );
};

export default ProductHeadline;
